import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Spinner,
    Image,
    useToast
} from '@chakra-ui/react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface Plan {
    number_of_days: number;
    amount: number;
    amount_off: number;
    plan_level: number;
}

const WechatPayment = () => {

    const [cookies] = useCookies(['Authorization']);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();

    const queryParams = new URLSearchParams(location.search);
    const planLevel = queryParams.get('plan_level') || '1';

    const [loading, setLoading] = useState(true);
    const [qrCode, setQrCode] = useState<string>('');
    const [orderId, setOrderId] = useState<string>('');
    const [polling, setPolling] = useState(false);
    const [expireTime, setExpireTime] = useState<string>('');
    const [planDays, setPlanDays] = useState<number | null>(null);
    const [planPrice, setPlanPrice] = useState<number | null>(null);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const resolveQrCode = (data: any) => {
        const rawCode = data?.picture_url || data?.qrcode || data?.qr_code || data?.code_url || '';
        if (typeof rawCode !== 'string') {
            return '';
        }

        const normalizedCode = rawCode.trim();
        if (!normalizedCode) {
            return '';
        }

        if (normalizedCode.startsWith('http://') || normalizedCode.startsWith('https://') || normalizedCode.startsWith('data:image')) {
            return normalizedCode;
        }

        return `data:image/png;base64,${normalizedCode}`;
    };

    const startPolling = useCallback((transactionId: string) => {
        setPolling(true);

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }

        pollIntervalRef.current = setInterval(async () => {
            try {
                const response = await fetch(process.env.REACT_APP_BASE_URL + 'check_payment_status', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': cookies.Authorization
                    },
                    body: JSON.stringify({
                        transaction_id: transactionId
                    })
                });

                const data = await response.json();
                const status = String(data?.status || data?.payment_status || '').toLowerCase();
                const isFailedStatus = ['failed', 'canceled', 'cancelled', 'expired', 'closed'].includes(status);
                // Keep old behavior compatibility: backend success flag is the primary signal.
                const isPaid = Boolean(data?.success);

                if (isPaid) {
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    setPolling(false);
                    toast({
                        position: "top",
                        title: t('checkout.payment_successful_toast_title'),
                        description: t('checkout.payment_successful_toast_description'),
                        status: "success",
                        duration: 5000,
                        isClosable: true,
                    });
                    navigate('/ReturnResult?success=1');
                } else if (!isPaid && isFailedStatus) {
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    setPolling(false);
                }
            } catch (error) {
                console.error('Polling error:', error);
            }
        }, 3000);
    }, [cookies.Authorization, navigate, t, toast]);

    const fetchPlanInfo = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_BASE_URL + 'get_all_plans', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': cookies.Authorization
                },
            });
            const data = await response.json();

            if (data?.success && Array.isArray(data?.plans)) {
                const currentPlanLevel = parseInt(planLevel, 10);
                const selectedPlan = data.plans.find((plan: Plan) => plan.plan_level === currentPlanLevel);
                if (selectedPlan) {
                    setPlanDays(selectedPlan.number_of_days);
                    setPlanPrice(selectedPlan.amount - selectedPlan.amount_off);
                }
            }
        } catch (error) {
            console.error('Fetch plan info error:', error);
        }
    }, [cookies.Authorization, planLevel]);

    const createOrder = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_BASE_URL + 'place_order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': cookies.Authorization
                },
                body: JSON.stringify({
                    plan_level: parseInt(planLevel, 10),
                    wechat_pay: true
                })
            });
            const data = await response.json();

            if (data.success) {
                const transactionId = data.transaction_id || data.order_id || '';
                setQrCode(resolveQrCode(data));
                setOrderId(transactionId);
                setExpireTime(data.expire_time || data.expires_at || '');
                const payableAmount = Number(data?.cny_amount ?? data?.amount);
                if (!Number.isNaN(payableAmount) && payableAmount > 0) {
                    setPlanPrice(payableAmount);
                }

                if (transactionId) {
                    startPolling(transactionId);
                }
            } else {
                toast({
                    position: 'top',
                    title: t('checkout.create_order_failed'),
                    description: data.error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                position: 'top',
                title: t('checkout.network_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [cookies.Authorization, planLevel, startPolling, t, toast]);

    useEffect(() => {
        createOrder();
        fetchPlanInfo();

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [createOrder, fetchPlanInfo]);

    if (loading) {
        return (
            <Box
                minH="calc(100vh - 64px)"
                bg="canvas"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.primary" thickness="4px" />
                    <Text color="muted">{t('checkout.qr_generating')}</Text>
                </VStack>
            </Box>
        );
    }

    return (
        <Box
            minH="calc(100vh - 64px)"
            bg="canvas"
            py={{ base: 8, md: 12 }}
        >
            <Container maxW={{ base: '100%', md: '560px' }} px={4}>
                <VStack spacing={8}>
                    <Box
                        p={{ base: 6, md: 8 }}
                        borderRadius="xl"
                        bg="surfaceCard"
                        border="1px solid"
                        borderColor="hairline"
                        textAlign="center"
                    >
                        <VStack spacing={6}>
                            <Heading size="lg" color="ink" letterSpacing={'-0.3px'}>
                                {t('checkout.wechat_pay_title')}
                            </Heading>

                            <Text color="muted" fontSize="sm">
                                {t('checkout.wechat_pay_instruction')}
                            </Text>

                            {qrCode && (
                                <Box
                                    p={4}
                                    borderRadius="xl"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="hairline"
                                    display="inline-block"
                                >
                                    <Image
                                        src={qrCode}
                                        alt={t('checkout.wechat_qr_alt')}
                                        width="200px"
                                        height="200px"
                                        objectFit="contain"
                                    />
                                </Box>
                            )}

                            <Box
                                p={4}
                                borderRadius="lg"
                                bg="surfaceSoft"
                                border="1px solid"
                                borderColor="hairline"
                                w="100%"
                            >
                                <VStack spacing={2}>
                                    <HStack justify="space-between" w="100%">
                                        <Text fontSize="sm" color="muted" whiteSpace="nowrap" flexShrink={0}>
                                            {t('checkout.order_id')}
                                        </Text>
                                        <Text fontSize="sm" fontWeight={500} color="bodyStrong">
                                            {orderId}
                                        </Text>
                                    </HStack>
                                    {expireTime && (
                                        <HStack justify="space-between" w="100%">
                                            <Text fontSize="sm" color="muted">
                                                {t('checkout.expires')}
                                            </Text>
                                            <Text fontSize="sm" fontWeight={500} color="bodyStrong">
                                                {expireTime}
                                            </Text>
                                        </HStack>
                                    )}
                                    {planDays !== null && (
                                        <HStack justify="space-between" w="100%">
                                            <Text fontSize="sm" color="muted">
                                                Plan
                                            </Text>
                                            <Text fontSize="sm" fontWeight={500} color="bodyStrong">
                                                {planDays} {t('checkout.day')}
                                            </Text>
                                        </HStack>
                                    )}
                                    {planPrice !== null && (
                                        <HStack justify="space-between" w="100%">
                                            <Text fontSize="sm" color="muted">
                                                Price
                                            </Text>
                                            <Text fontSize="sm" fontWeight={500} color="bodyStrong">
                                                ¥{planPrice.toFixed(2)}
                                            </Text>
                                        </HStack>
                                    )}
                                </VStack>
                            </Box>

                            {polling && (
                                <HStack spacing={2}>
                                    <Spinner size="sm" color="success" />
                                    <Text fontSize="sm" color="muted">
                                        {t('checkout.waiting_for_payment')}
                                    </Text>
                                </HStack>
                            )}

                            <Text fontSize="xs" color="mutedSoft">
                                {t('checkout.complete_within_30_minutes')}
                            </Text>
                        </VStack>
                    </Box>

                    <HStack spacing={4} w="100%">
                        <Button
                            variant="outline"
                            flex={1}
                            onClick={() => navigate('/Subscription')}
                            borderColor="hairline"
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            flex={1}
                            onClick={() => navigate('/')}
                        >
                            {t('checkout.back_to_home')}
                        </Button>
                    </HStack>
                </VStack>
            </Container>
        </Box>
    );
};

export default WechatPayment;
